// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL BOTÓN DEL RELOJ EN LA BARRA DE ARRIBA                                                     (t271, 16-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Un icono de reloj con su texto de ayuda «Reloj», en la barra de arriba de TODAS las pantallas de la clínica
//  (components/Layout.jsx: la cabecera normal y la del Modo doctor, que es la del gabinete). Al pulsarlo se abre la
//  ventana del reloj ENCIMA de la página (context/RelojContext.jsx la monta en la raíz), sin cambiar de página.
//  Con una cuenta atrás o un cronómetro en marcha, el botón enseña el tiempo («02:41») para verlo sin abrir nada: en el
//  color de la clínica en marcha, con el borde a trazos en pausa y en rojo, latiendo, cuando se ha cumplido. Donde la
//  barra va justa (la tablet y el móvil: `soloNumero`), en marcha va el número solo, sin el icono, para que quepa.
//
//  SIN HOOKS, A PROPÓSITO (como utils/contactoBiango.jsx): la cabecera la montan decenas de guardias con sus dobles y
//  expanden a mano lo que hay dentro; un componente con hooks no se deja expandir así. Por eso el número no lo pinta
//  React: el botón se engancha al reloj (utils/reloj.js) por su `ref`, y el reloj le escribe el texto y el estado en el
//  propio elemento, solo cuando cambia el segundo. Así, además, la cabecera entera NO se vuelve a pintar cada segundo
//  (es la pieza más grande de la app): solo cambia un texto. Los estilos de cada estado van en una hoja propia, puesta
//  una vez en <head> (`ponerEstilos`), y no en un <style> dentro de la cabecera (las guardias leen el <style> de Layout
//  como el primero que hay en ella). Lo cargan con el React de verdad las guardias que pintan la cabecera:
//  @pintarJsx-con-react (test-helpers/pintarJsx.js, utilsJsxPropios).
//
//  COLORES: los de la clínica (`--primary`, `--sobre-primary`) y los tokens de la casa; ninguno escrito a mano salvo el
//  blanco sobre el rojo de aviso. Con prefers-reduced-motion, el rojo no late.
//
//  t271b · CUANDO NO CABE EN LA BARRA (un móvil estrecho: components/Layout.jsx lo mide, nivel 6), el reloj va al menú del
//  avatar y lo que está en marcha sale en una FRANJA debajo de la barra (`FranjaReloj`): «Cuenta atrás 02:41 · Abrir», con
//  los mismos colores que el botón y del mismo pintor. Sin nada en marcha no se ve.
import { elReloj, abrirReloj, paraElBoton } from './reloj'
import { t, marca, tDato } from './i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const TXT_RELOJ = marca('utils.relojCabecera.reloj', 'Reloj')
const ID_ESTILOS = 'biango-reloj-cabecera'

export const CSS_BOTON_RELOJ = [
  '[data-boton-reloj] [data-reloj-cuenta]:empty { display: none; }',
  '[data-boton-reloj][data-estado="marcha"], [data-boton-reloj][data-estado="crono"] { background: var(--primary) !important; border-color: var(--primary) !important; color: var(--sobre-primary) !important; }',
  '[data-boton-reloj][data-estado="pausa"], [data-boton-reloj][data-estado="crono-pausa"] { border-style: dashed !important; border-color: var(--borde-control) !important; color: var(--text-secondary) !important; }',
  '[data-boton-reloj][data-estado="cumplida"] { background: var(--danger) !important; border-color: var(--danger) !important; color: #fff !important; animation: biango-reloj-latido 1s steps(2, jump-none) infinite; }',
  '@keyframes biango-reloj-latido { from { opacity: 1; } to { opacity: .6; } }',
  ['marcha', 'pausa', 'cumplida', 'crono', 'crono-pausa'].map((e) => '[data-boton-reloj][data-solo-numero="si"][data-estado="' + e + '"] svg').join(', ') + ' { display: none; }',
  '[data-boton-reloj]:focus-visible { outline: 2px solid var(--foco-aro); outline-offset: 2px; }',
  // t271b · la franja de debajo de la barra: solo se ve con algo en marcha, y con los colores del botón.
  ['marcha', 'pausa', 'cumplida', 'crono', 'crono-pausa'].map((e) => '[data-franja-reloj][data-estado="' + e + '"]').join(', ') + ' { display: flex !important; }',
  '[data-franja-reloj][data-estado="marcha"], [data-franja-reloj][data-estado="crono"] { background: var(--primary) !important; color: var(--sobre-primary) !important; }',
  '[data-franja-reloj][data-estado="cumplida"] { background: var(--danger) !important; color: #fff !important; }',
  '[data-franja-reloj]:focus-visible { outline: 2px solid var(--foco-aro); outline-offset: -2px; }',
  '@media (prefers-reduced-motion: reduce) { [data-boton-reloj][data-estado="cumplida"] { animation: none; } }',
].join('\n')

function ponerEstilos(doc) {
  try {
    if (!doc || typeof doc.getElementById !== 'function' || doc.getElementById(ID_ESTILOS)) return
    const s = doc.createElement('style')
    s.id = ID_ESTILOS
    s.textContent = CSS_BOTON_RELOJ
    ;(doc.head || doc.documentElement).appendChild(s)
  } catch (_) { /* sin hoja propia el botón se ve igual, sin los colores de cada estado */ }
}

// Los botones pintados ahora mismo (la cabecera normal o la del Modo doctor; al cambiar de una a otra, dos un instante).
const botones = new Set()
let desuscribir = null

// Escribe en cada botón lo que toca: el estado (para la hoja de estilos), el número, la etiqueta y el título. Solo
// toca el DOM cuando algo cambia: cuatro veces por segundo se pregunta, una por segundo se escribe.
export function pintarBotonesReloj() {
  const r = elReloj()
  const d = paraElBoton(r.estado(), r.ahora())
  for (const b of [...botones]) {
    try {
      if (b.isConnected === false) { botones.delete(b); continue }
      if (b.getAttribute('data-estado') !== d.estado) b.setAttribute('data-estado', d.estado)
      const n = b.querySelector('[data-reloj-cuenta]')
      if (n && n.textContent !== d.texto) n.textContent = d.texto
      const q = b.querySelector('[data-reloj-que]')
      if (q && q.textContent !== d.que) q.textContent = d.que
      if (b.getAttribute('aria-label') !== d.etiqueta) { b.setAttribute('aria-label', d.etiqueta); b.setAttribute('title', d.etiqueta) }
    } catch (_) { botones.delete(b) }
  }
  if (!botones.size && desuscribir) { desuscribir(); desuscribir = null }
}

// El `ref` del botón: lo apunta, lo pinta ya (sin esperar al segundo siguiente) y, al quitarse, lo borra (React 19
// llama a lo que devuelve; si llegara un null, se limpian los que ya no están en la página).
export function engancharBotonReloj(el) {
  if (!el) { pintarBotonesReloj(); return undefined }
  botones.add(el)
  ponerEstilos(el.ownerDocument)
  if (!desuscribir) {
    const r = elReloj()
    const a = r.suscribir(pintarBotonesReloj)
    const b = r.alLatir(pintarBotonesReloj)
    desuscribir = () => { a(); b() }
  }
  pintarBotonesReloj()
  return () => {
    botones.delete(el)
    if (!botones.size && desuscribir) { desuscribir(); desuscribir = null }
  }
}

export function BotonReloj({ soloNumero = false } = {}) {
  return (
    <button type="button" data-boton-reloj="" data-solo-numero={soloNumero ? 'si' : 'no'} ref={engancharBotonReloj}
      onClick={() => abrirReloj()} title={tDato(TXT_RELOJ)} aria-label={tDato(TXT_RELOJ)} aria-haspopup="dialog"
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: '32px', height: '32px',
        padding: '0 7px', boxSizing: 'border-box', borderRadius: '9px', border: '1px solid var(--border)', background: 'var(--surface-2)',
        color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0, fontSize: '13px', fontWeight: 800, lineHeight: 1,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="13.5" r="7.5" />
        <path d="M12 10v3.5l2.4 1.6" />
        <path d="M9.5 3h5" />
        <path d="M12 3v3" />
      </svg>
      <span data-reloj-cuenta="" />
    </button>
  )
}

// t271b · LA FRANJA, cuando el botón no cabe en la barra. Va oculta (`display: none`) y la enseña la hoja de estilos de
// arriba en cuanto el reloj le pone un estado: así la ven igual de oculta las guardias que miden la pantalla. Solo con
// `activa` (el reloj no cabe en la barra); si no, no pinta nada.
export function FranjaReloj({ activa = false } = {}) {
  if (!activa) return null
  return (
    <button type="button" data-franja-reloj="" ref={engancharBotonReloj} onClick={() => abrirReloj()} title={tDato(TXT_RELOJ)} aria-label={tDato(TXT_RELOJ)}
      aria-haspopup="dialog"
      style={{ display: 'none', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', minHeight: '38px', padding: '6px 16px',
        boxSizing: 'border-box', border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-primary)',
        fontSize: '14px', fontWeight: 800, cursor: 'pointer', flexShrink: 0, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="13.5" r="7.5" />
        <path d="M12 10v3.5l2.4 1.6" />
        <path d="M9.5 3h5" />
        <path d="M12 3v3" />
      </svg>
      <span data-reloj-que="" />
      <span data-reloj-cuenta="" />
      <span style={{ fontWeight: 600, opacity: 0.85 }}>{t('utils.relojCabecera.abrir', '· Abrir')}</span>
    </button>
  )
}
